import { TPickupPointProvider } from 'models/orders'
import { IYandexDropoffStation } from 'models/yandexDelivery'
import type { IOzonDropoffLocation, IOzonDropoffPoint } from 'api/ozonDelivery'

export type TCoords = [number, number]

export interface ICityOption {
  name: string
  latitude: number
  longitude: number
}

export interface IMapPoint {
  code: string
  name: string
  address: string
  workTime?: string
  coords: TCoords
}

// Пункт СДЭК из списка офисов и пункт, сохранённый у продавца, — общие поля
interface ICdekPointSource {
  code: string
  name: string
  work_time?: string
  location: {
    address: string
    address_full?: string
    latitude: number
    longitude: number
  }
}

export const DEFAULT_CENTER: TCoords = [55.755864, 37.617698]
export const CITY_ZOOM = 9.5
export const BOUNDS_DELAY = 450

export const MAP_LOOK: Record<
  TPickupPointProvider,
  { preset: string; clusterPreset: string; gridSize: number }
> = {
  CDEK: {
    preset: 'islands#greenDotIcon',
    clusterPreset: 'islands#greenClusterIcons',
    gridSize: 32
  },
  YANDEX: {
    preset: 'islands#yellowDotIcon',
    clusterPreset: 'islands#yellowClusterIcons',
    gridSize: 64
  },
  OZON: {
    preset: 'islands#blueDotIcon',
    clusterPreset: 'islands#blueClusterIcons',
    gridSize: 64
  }
}
export const CURRENT_PRESET = 'islands#redDotIcon'
export const PICKED_PRESET = 'islands#blueDotIcon'

// СДЭК приходит регионом целиком, поэтому при сдвиге карты догружаем, только когда её
// увели туда, где ближайший загруженный пункт дальше этого расстояния
export const CDEK_LOADED_RADIUS_KM = 15
// Клетка в полградуса: пустое место (лес, море) запрашиваем один раз, а не при каждом сдвиге
const CDEK_CELLS_PER_DEGREE = 2

export const fromCdekOffice = (office: ICdekPointSource): IMapPoint => ({
  code: office.code,
  name: office.name,
  address: office.location.address_full ?? office.location.address,
  workTime: office.work_time,
  coords: [office.location.latitude, office.location.longitude]
})

export const fromYandexStation = (station: IYandexDropoffStation): IMapPoint => ({
  code: station.code || station.uuid,
  name: station.name,
  address:
    station.location.address_full ?? station.location.address ?? station.name,
  workTime: station.work_time,
  coords: [station.location.latitude, station.location.longitude]
})

const asCoordinate = (value: number | string | undefined) => {
  if (value === undefined || value === '') return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

const getOzonLocations = (point: IOzonDropoffPoint): IOzonDropoffLocation[] => [
  point.location,
  point.coordinates,
  point.address && typeof point.address === 'object' ? point.address : undefined
].filter((location): location is IOzonDropoffLocation => location !== undefined)

export const ozonDropoffAddress = (point: IOzonDropoffPoint): string => {
  const locations = getOzonLocations(point)
  return point.full_address ??
    locations.map((location) => location.full_address ?? location.address).find(Boolean) ??
    (typeof point.address === 'string' ? point.address : undefined) ??
    point.name ?? `Пункт № ${point.dropoff_point_id}`
}

export const fromOzonDropoffPoint = (point: IOzonDropoffPoint): IMapPoint | null => {
  const locations = getOzonLocations(point)
  const latitude = asCoordinate(point.latitude ??
    locations.map((location) => location.latitude ?? location.lat).find((value) => value !== undefined))
  const longitude = asCoordinate(point.longitude ??
    locations.map((location) => location.longitude ?? location.lon ?? location.lng)
      .find((value) => value !== undefined))
  if (!Number.isSafeInteger(Number(point.dropoff_point_id)) || Number(point.dropoff_point_id) <= 0 ||
    latitude === null || longitude === null || Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null
  return {
    code: String(point.dropoff_point_id),
    name: point.name ?? `Пункт № ${point.dropoff_point_id}`,
    address: ozonDropoffAddress(point),
    workTime: point.work_time,
    coords: [latitude, longitude]
  }
}

// Долгота сходится к полюсам, поэтому её разницу масштабируем по широте
export const distanceKm = (from: TCoords, to: TCoords) =>
  Math.hypot(
    (from[0] - to[0]) * 111,
    (from[1] - to[1]) * 111 * Math.cos((from[0] * Math.PI) / 180)
  )

export const getCdekCell = ([latitude, longitude]: TCoords) =>
  `${Math.round(latitude * CDEK_CELLS_PER_DEGREE)}:${Math.round(longitude * CDEK_CELLS_PER_DEGREE)}`
